"use client";

import { motion, useInView } from "framer-motion";
import { useRef } from "react";
import { Brain, History, GitMerge, MessageSquare, Database } from "lucide-react";

export function RepoMemorySection() {
    const ref = useRef(null);
    const isInView = useInView(ref, { once: true, margin: "-100px" });

    return (
        <section ref={ref} className="py-24 px-4 bg-background border-t border-border overflow-hidden">
            <div className="container mx-auto max-w-6xl">
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
                    <motion.div
                        initial={{ opacity: 0, x: -20 }}
                        animate={isInView ? { opacity: 1, x: 0 } : { opacity: 0, x: -20 }}
                        transition={{ duration: 0.6 }}
                    >
                        <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-primary/10 text-primary text-xs font-medium mb-6">
                            <Database className="w-3 h-3" />
                            <span>Contextual Intelligence</span>
                        </div>
                        <h2 className="text-4xl md:text-5xl font-bold mb-6 tracking-tight text-zinc-900 dark:text-white">
                            repo memory
                        </h2>
                        <p className="text-xl text-zinc-600 dark:text-zinc-400 mb-8 leading-relaxed">
                            burg ai learns from your merged PRs, past reviews, and decisions to reduce noise over time.
                        </p>

                        <div className="space-y-6">
                            {[
                                {
                                    icon: GitMerge,
                                    title: "learns from merged code",
                                    desc: "analyzes accepted patterns in your codebase to understand what 'good' looks like."
                                },
                                {
                                    icon: History,
                                    title: "remembers past feedback",
                                    desc: "won't nag you about things you've dismissed before. it respects your decisions."
                                },
                                {
                                    icon: MessageSquare,
                                    title: "adapts to team style",
                                    desc: "mimics your team's tone, priorities, and unwritten rules."
                                }
                            ].map((item, i) => (
                                <motion.div
                                    key={i}
                                    initial={{ opacity: 0, y: 10 }}
                                    animate={isInView ? { opacity: 1, y: 0 } : { opacity: 0, y: 10 }}
                                    transition={{ duration: 0.4, delay: 0.2 + (i * 0.1) }}
                                    className="flex items-start gap-4"
                                >
                                    <div className="p-2 rounded-lg bg-zinc-100 dark:bg-zinc-800 text-primary mt-1">
                                        <item.icon className="w-5 h-5" />
                                    </div>
                                    <div>
                                        <h3 className="font-semibold text-lg text-zinc-900 dark:text-white mb-1">{item.title}</h3>
                                        <p className="text-zinc-600 dark:text-zinc-400 text-sm leading-relaxed">{item.desc}</p>
                                    </div>
                                </motion.div>
                            ))}
                        </div>
                    </motion.div>

                    <motion.div
                        initial={{ opacity: 0, scale: 0.95 }}
                        animate={isInView ? { opacity: 1, scale: 1 } : { opacity: 0, scale: 0.95 }}
                        transition={{ duration: 0.6, delay: 0.2 }}
                        className="relative"
                    >
                        {/* Decorative background elements */}
                        <div className="absolute -top-20 -right-20 w-64 h-64 bg-primary/20 rounded-full blur-3xl opacity-20"></div>
                        <div className="absolute -bottom-20 -left-20 w-64 h-64 bg-blue-500/20 rounded-full blur-3xl opacity-20"></div>

                        <div className="relative rounded-2xl overflow-hidden border border-zinc-200 dark:border-border bg-white dark:bg-zinc-900/50 shadow-2xl p-8 min-h-[440px] flex flex-col items-center justify-center">

                            <div className="relative z-10 w-full max-w-sm space-y-4">
                                <div className="text-xs font-mono text-center text-muted-foreground mb-4">
                                    {/* continuous learning stream */}
                                </div>

                                <MemoryCard
                                    text="PR #842: Accepted 'any' in test files"
                                    type="learning"
                                    delay={0.4}
                                />
                                <MemoryCard
                                    text="User dismissed: 'Add JSDoc' for utils"
                                    type="adjustment"
                                    delay={0.6}
                                />
                                <MemoryCard
                                    text="Pattern learned: Controllers use AsyncHandler"
                                    type="optimization"
                                    delay={0.8}
                                />
                                <MemoryCard
                                    text="Feedback styles: concise, direct"
                                    type="learning"
                                    delay={1.0}
                                />

                                <motion.div
                                    initial={{ opacity: 0, y: 10 }}
                                    animate={isInView ? { opacity: 1, y: 0 } : { opacity: 0, y: 10 }}
                                    transition={{ delay: 1.2, duration: 0.5 }}
                                    className="mt-6 p-4 rounded-xl border border-dashed border-zinc-300 dark:border-zinc-700 bg-zinc-50 dark:bg-zinc-800/50 text-center"
                                >
                                    <div className="flex items-center justify-center gap-2 text-sm font-medium text-zinc-900 dark:text-white mb-1">
                                        <Brain className="w-4 h-4 text-primary" />
                                        <span>Knowledge Base Updated</span>
                                    </div>
                                    <p className="text-xs text-muted-foreground">
                                        Next review will be more personalized.
                                    </p>
                                </motion.div>
                            </div>
                        </div>
                    </motion.div>
                </div>
            </div>
        </section>
    );
}

function MemoryCard({ text, type, delay }: { text: string, type: 'learning' | 'adjustment' | 'optimization', delay: number }) {
    const colors = {
        learning: "bg-blue-500",
        adjustment: "bg-orange-500",
        optimization: "bg-green-500"
    };

    return (
        <motion.div
            initial={{ opacity: 0, x: 20 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
            transition={{ delay, duration: 0.5 }}
            className="p-4 rounded-xl bg-white dark:bg-zinc-950 border border-zinc-100 dark:border-zinc-800 shadow-sm flex items-center gap-3 backdrop-blur-sm"
        >
            <div className={`w-2 h-2 rounded-full flex-shrink-0 ${colors[type]}`} />
            <span className="text-sm font-mono text-zinc-700 dark:text-zinc-300 truncate">{text}</span>
            {type === 'learning' && <GitMerge className="w-3 h-3 ml-auto text-muted-foreground opacity-50" />}
            {type === 'adjustment' && <History className="w-3 h-3 ml-auto text-muted-foreground opacity-50" />}
            {type === 'optimization' && <Brain className="w-3 h-3 ml-auto text-muted-foreground opacity-50" />}
        </motion.div>
    );
}
